import React, { useState, useEffect } from "react";
import { Box, Container, Grid, Paper, Typography, Tabs, Tab, Button, Collapse } from "@mui/material";
import Header from "../../components/GSO/Header";
import { useSidebar } from "../../components/SidebarContext";
import { getApiUrl, getApiHeaders } from "../../config/api";
import { auth } from "../../config/firebaseConfig";
import TotalItemsReported from "./TotalItemsReported";
import PendingClaims from "./PendingClaims";
import MostCommonLostItems from "./MostCommonLostItems";
import TotalItemsReturned from "./TotalItemsReturned";
import HotspotLocations from "./HotspotLocations";
import PeakTimeLostItems from "./PeakTimeLostItems";

const dashboardTabs = [
  { label: "Total Items Reported", component: (props) => <TotalItemsReported {...props} /> },
  { label: "Pending Claims", component: (props) => <PendingClaims {...props} /> },
  { label: "Most Common Lost Items", component: (props) => <MostCommonLostItems {...props} /> },
  { label: "Total Items Returned", component: (props) => <TotalItemsReturned {...props} /> },
  { label: "Hotspot Locations", component: (props) => <HotspotLocations {...props} /> },
  { label: "Peak Time of Lost Items", component: (props) => <PeakTimeLostItems {...props} /> },
];

const Dashboard = () => {
  const { isSidebarOpen } = useSidebar();
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;
  const [selectedTab, setSelectedTab] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    totalItemsReported: "...",
    pendingClaims: "...",
    mostCommonItem: "...",
    totalItemsReturned: "...",
    topHotspot: "...",
    peakTime: "..."
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      const token = await user.getIdToken();
      
      // Fetch items data from found-item/list
      const itemsResponse = await fetch(getApiUrl("found-item/list"), {
        headers: getApiHeaders({ Authorization: `Bearer ${token}` })
      });
      const itemsData = await itemsResponse.json();
      
      // Fetch claims data from api/claims/all
      const claimsResponse = await fetch(getApiUrl("api/claims/all"), {
        headers: getApiHeaders({ Authorization: `Bearer ${token}` })
      });
      const claimsData = await claimsResponse.json();
      
      console.log("Dashboard - Items data:", itemsData);
      console.log("Dashboard - Claims data:", claimsData);
      
      // Calculate statistics
      const totalItems = itemsData.items?.length || 0;
      const pendingClaims = claimsData.claims?.filter(c => c.status === 'pending')?.length || 0;
      const returnedItems = claimsData.claims?.filter(c => c.status === 'approved' || c.status === 'completed')?.length || 0;
      
      // Find most common item type
      const itemTypes = itemsData.items?.map(item => item.item_type) || [];
      const typeCounts = itemTypes.reduce((acc, type) => {
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      const mostCommon = Object.keys(typeCounts).length > 0 
        ? Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0][0]
        : "N/A";
      
      // Find top hotspot location (most items found)
      const locations = itemsData.items?.map(item => item.location_found) || [];
      const locationCounts = locations.reduce((acc, location) => {
        if (location) {
          acc[location] = (acc[location] || 0) + 1;
        }
        return acc;
      }, {});
      const topHotspot = Object.keys(locationCounts).length > 0
        ? Object.entries(locationCounts).sort((a, b) => b[1] - a[1])[0][0]
        : "N/A";
      
      // Find peak time (most common hour items are found)
      const times = itemsData.items?.map(item => {
        if (item.time_found) {
          return parseInt(item.time_found.split(':')[0]);
        }
        return null;
      }).filter(t => t !== null) || [];
      
      const timeCounts = times.reduce((acc, hour) => {
        acc[hour] = (acc[hour] || 0) + 1;
        return acc;
      }, {});
      
      const peakHour = Object.keys(timeCounts).length > 0
        ? Object.entries(timeCounts).sort((a, b) => b[1] - a[1])[0][0]
        : null;
      
      const peakTime = peakHour !== null
        ? `${peakHour}:00 - ${parseInt(peakHour) + 1}:00`
        : "N/A";
      
      console.log("Dashboard stats:", {
        totalItems,
        pendingClaims,
        mostCommon,
        returnedItems,
        topHotspot,
        peakTime
      });
      
      setDashboardStats({
        totalItemsReported: totalItems,
        pendingClaims: pendingClaims,
        mostCommonItem: mostCommon,
        totalItemsReturned: returnedItems,
        topHotspot: topHotspot,
        peakTime: peakTime
      });
      
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    }
  };

  const dashboardData = [
    { title: "Total Items Reported", value: dashboardStats.totalItemsReported, bgColor: "#FBFFE9", textColor: "#526D50" },
    { title: "Pending Claims", value: dashboardStats.pendingClaims, bgColor: "#526D50", textColor: "#FBFFE9" },
    { title: "Most Common Lost Items", value: dashboardStats.mostCommonItem, bgColor: "#FBFFE9", textColor: "#526D50" },
    { title: "Total Items Returned", value: dashboardStats.totalItemsReturned, bgColor: "#526D50", textColor: "#FBFFE9" },
    { title: "Top Hotspot Location", value: dashboardStats.topHotspot, bgColor: "#FBFFE9", textColor: "#526D50" },
    { title: "Peak Time of Lost Items", value: dashboardStats.peakTime, bgColor: "#526D50", textColor: "#FBFFE9" },
  ];

  return (
    <Box
      sx={{
        bgcolor: "#FBFFE9",
        width: "100vw",
        transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        maxWidth: isSidebarOpen ? (isMobile ? "calc(100vw - 200px)" : "calc(100vw - 300px)") : "100vw",
        overflow: "hidden",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}
    >
  <Header title="Dashboard" />
      
      {/* First Section - Cards */}
      <Box 
        sx={{ 
          position: "absolute",
          top: "64px",
          left: 0,
          right: 0,
          bottom: 0,
          transform: selectedTab !== null ? "translateY(-100%)" : "translateY(0)",
          transition: "transform 0.5s ease-in-out",
          zIndex: 1,
          overflow: "hidden"
        }}
      >
        <Container maxWidth="xl" sx={{ px: 6, py: 3, height: "100%" }}>
          <Paper 
            elevation={4} 
            sx={{ 
              bgcolor: "#E1ECC8", 
              p: 4, 
              borderRadius: "25px", 
              height: "100%",
              display: 'flex',
              alignItems: 'center',
              overflow: "hidden"
            }}
          >
            <Grid 
              container 
              spacing={3} 
              alignItems="center" 
              justifyContent="center"
              sx={{ 
                maxHeight: "100%",
                margin: 0,
                width: "100%"
              }}
            >
              {dashboardData.map((item, index) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 2
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      bgcolor: item.bgColor,
                      borderRadius: "15px",
                      width: "100%",
                      maxWidth: 350,
                      height: 280,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 3,
                      cursor: 'pointer',
                      border: selectedTab === index ? '2px solid #526D50' : 'none',
                      boxShadow: selectedTab === index ? '0 0 0 2px #E1ECC8' : 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
                      }
                    }}
                    onClick={() => setSelectedTab(index)}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: item.textColor,
                        textAlign: "center",
                        fontWeight: "bold",
                        mb: 2,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        color: item.textColor,
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Second Section - Tab View */}
      <Box 
        sx={{ 
          position: "absolute",
          top: "64px",
          left: 0,
          right: 0,
          bottom: 0,
          transform: selectedTab !== null ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.5s ease-in-out",
          visibility: selectedTab !== null ? "visible" : "hidden",
          overflow: "hidden"
        }}
      >
        <Container maxWidth="xl" sx={{ px: 6, py: 3, height: "100%" }}>
          <Paper elevation={4} sx={{ bgcolor: "#E1ECC8", p: 2, borderRadius: "25px", height: "100%", display: 'flex', flexDirection: 'column', overflow: "hidden" }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Tabs
                value={selectedTab ?? 0}
                onChange={(_, newValue) => setSelectedTab(newValue)}
                variant={isMobile ? "scrollable" : "fullWidth"}
                scrollButtons={isMobile ? "auto" : false}
                sx={{
                  flex: 1,
                  borderBottom: 1,
                  borderColor: "divider",
                  ".MuiTab-root": {
                    fontWeight: 600,
                    color: "#526D50",
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: isMobile ? "0.9rem" : "1.1rem",
                    textTransform: "none",
                    minHeight: 48,
                  },
                  ".Mui-selected": {
                    color: "#A0C49D",
                  },
                  bgcolor: "#FBFFE9",
                  borderRadius: "20px 20px 0 0",
                }}
                TabIndicatorProps={{ style: { background: "#A0C49D", height: 4, borderRadius: 2 } }}
              >
                {dashboardTabs.map((tab) => (
                  <Tab key={tab.label} label={tab.label} />
                ))}
              </Tabs>
              <Button
                variant="outlined"
                sx={{ 
                  ml: 2, 
                  color: "#526D50", 
                  borderColor: "#A0C49D", 
                  fontWeight: 600, 
                  borderRadius: 2, 
                  textTransform: 'none',
                  minWidth: 100
                }}
                onClick={() => setSelectedTab(null)}
              >
                Close
              </Button>
            </Box>
            <Box 
              sx={{ 
                flex: 1, 
                overflow: 'auto', 
                mt: 3,
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                '-ms-overflow-style': 'none',
                'scrollbarWidth': 'none'
              }}
            >
              {selectedTab !== null && dashboardTabs[selectedTab].component({ isTabView: true })}
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;
